import { getEnv } from "@/src/config/env";
import type { PhotoPackTier } from "@/src/core/domain/types";
import { PHOTO_PACK_CATALOG, PHOTO_PACK_TIERS } from "@/src/core/domain/types";
import { photosForTier, roundsForTier } from "@/src/core/packs/pack-policy";

export type SupportedBillingCurrency = "usd" | "ars";

export interface PackPriceEntry {
  usd: number;
  ars: number;
}

export const PHOTO_PACK_PRICES: Record<PhotoPackTier, PackPriceEntry> = {
  pack_6: { usd: 9.99, ars: 14_500 },
  pack_12: { usd: 12.05, ars: 17_500 },
  pack_24: { usd: 16.5, ars: 23_900 },
};

export interface PackQuote {
  tier: PhotoPackTier;
  photos: number;
  rounds: number;
  currency: SupportedBillingCurrency;
  basePriceMajor: number;
  totalAmountMajor: number;
  percentOff: number;
  promoCode: string | null;
  formattedBasePrice: string;
  formattedPrice: string;
}

export function resolveEffectiveBillingCurrency(): SupportedBillingCurrency {
  const env = getEnv();
  return env.DEV_OVERRIDE_BILLING_CURRENCY ?? env.BILLING_CURRENCY;
}

export function quotePackPurchase(input: {
  tier: PhotoPackTier;
  currency: SupportedBillingCurrency;
  /** Afecta separadores numéricos en el precio mostrado. */
  priceLocale?: "es" | "en";
  promo?: { percentOff: number; code: string } | null;
}): PackQuote {
  const price = PHOTO_PACK_PRICES[input.tier];
  const symbol = input.currency === "ars" ? "ARS $" : "USD $";
  const numberLocale = input.priceLocale === "en" ? "en-US" : "es-AR";
  const baseAmount = price[input.currency];

  const percentOff = input.promo?.percentOff ?? 0;
  const promoCode = input.promo?.code ?? null;
  const boundedPercentOff = Number.isFinite(percentOff)
    ? Math.max(0, Math.min(99, Math.round(percentOff)))
    : 0;

  const rawDiscount = (baseAmount * boundedPercentOff) / 100;
  const discount =
    input.currency === "usd" ? Math.round(rawDiscount * 100) / 100 : Math.round(rawDiscount);
  const finalAmountRaw = Math.max(0, baseAmount - discount);
  const finalAmount =
    input.currency === "usd" ? Math.round(finalAmountRaw * 100) / 100 : finalAmountRaw;

  const formattedBasePrice = `${symbol}${baseAmount.toLocaleString(numberLocale)}`;
  const formattedPrice = `${symbol}${finalAmount.toLocaleString(numberLocale)}`;
  return {
    tier: input.tier,
    photos: photosForTier(input.tier),
    rounds: roundsForTier(input.tier),
    currency: input.currency,
    basePriceMajor: baseAmount,
    totalAmountMajor: finalAmount,
    percentOff: boundedPercentOff,
    promoCode,
    formattedBasePrice,
    formattedPrice,
  };
}

/** Lista de packs con precio (mensajes WhatsApp / copy de agotado). */
export function buildWhatsappPackPriceBulletList(
  locale: "es" | "en" = "es",
  promo?: { percentOff: number; code: string } | null,
  options?: { currency?: SupportedBillingCurrency },
): string {
  const currency = options?.currency ?? resolveEffectiveBillingCurrency();
  return PHOTO_PACK_TIERS.map((tier) =>
    buildPackQuoteWhatsappSummary(
      quotePackPurchase({ tier, currency, priceLocale: locale, promo: promo ?? null }),
      locale,
    ),
  ).join("\n");
}

/** Precio para copy custom (WhatsApp): tachado + final si aplica promo; un solo lugar de verdad. */
export function formatQuotedPackPriceForMessaging(quote: PackQuote): string {
  const hasPromo = quote.percentOff > 0 && quote.formattedBasePrice !== quote.formattedPrice;
  return hasPromo
    ? `~${quote.formattedBasePrice}~ → *${quote.formattedPrice}* (-${quote.percentOff}%)`
    : quote.formattedPrice;
}

export function buildPackQuoteWhatsappSummary(quote: PackQuote, locale: "es" | "en" = "es"): string {
  const formatted = formatQuotedPackPriceForMessaging(quote);

  if (quote.tier === "pack_12") {
    if (locale === "en") {
      return [
        `🔥 *12-photo pack · TOP PICK* — ${formatted}`,
        `   _⭐ Best balance · 2 rounds of variants_`,
      ].join("\n");
    }
    return [
      `🔥 *Pack de 12 fotos · RECOMENDADO* — ${formatted}`,
      `   _⭐ Equilibrio ideal · 2 rondas de 6 fotos_`,
    ].join("\n");
  }

  if (locale === "en") {
    return `📦 *Pack ${quote.photos} photos* — ${formatted}`;
  }
  return `📦 *Pack de ${quote.photos} fotos* — ${formatted}`;
}
